<template>
  <div class="p-3 w-full">
    <div class="px-2 pb-3 mt-1 border rounded-md shadow-sm dark:border-dark-divider-border">
      <div class="my-2 font-semibold border dark:border-dark-divider-border">
        <TextInput
          :no-border="true"
          name="note_title"
          :value="new_title"
          :is-box-background="true"
          type="text"
          :placeholder="$t('Title')"
          :input-classes="`rounded-md appearance-none block w-full px-3 py-2  placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm`"
          @input="
            $utils.debounce(() => {
              new_title = $event.target.value
            }, 800)
          " />
      </div>
      <div class="mt-0">
        <div class="h-24 mt-0 border-0">
          <TextEditor
            :value="new_description"
            :show-label="false"
            :no-border="true"
            toolbar="essential"
            :placeholder="$t('Note description')"
            name="new_description"
            :label="$t('Description')"
            theme="snow"
            input-classes="rounded-b-md"
            @changed="
              (e) => {
                new_description = e
              }
            " />
        </div>
      </div>

      <div class="flex items-center dark:border-dark-divider-border relative top-16">
        <div class="dark:text-white">
          {{ $t("Background") }}:
        </div>
        <div
          :style="{ backgroundColor: new_bg_color }"
          :class="[`w-8 h-8 rounded-full ml-10 mt-1 ${new_bg_color == 'transparent' ? 'border dark:border-dark-divider-border' : ''}`]">
          <span v-if="new_bg_color == 'transparent'" class="text-4xl text-red-400 ml-[8px] font-light relative bottom-1">/</span>
        </div>
        <Menu as="div" class="w-8 h-8 -ml-7 overflow-hidden rounded-full">
          <MenuButton class="text-4xl opacity-0">
            +
          </MenuButton>

          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0">
            <MenuItems
              as="ul"
              class="z-20 absolute h-[200px] overflow-auto top-0 left-0 mt-2 w-56 rounded-lg shadow-lg bg-light-screen-background dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none transition ease-in-out duration-side-menu-transition-duration">
              <div class="">
                <MenuItem v-for="color in color_palettes" :key="color" v-slot="{ active }" as="li">
                  <button
                    type="button"
                    :class="[
                      active
                        ? 'bg-light-brand-blue dark:bg-dark-brand-blue text-dark-body-text'
                        : 'text-light-body-text dark:text-dark-body-text',
                      'w-full group sm:text-sm flex items-center px-4 py-2 disabled:bg-common-disable-bg disabled:cursor-default disabled:opacity-60 rounded-lg transition ease-in-out duration-side-menu-transition-duration has-tooltip'
                    ]"
                    @click="
                      () => {
                        new_bg_color = color.hex
                      }
                    ">
                    <span
                      class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md mt-10 tooltip whitespace-nowrap text-common-grey bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                      {{ color.hex }}</span>
                    <span :class="[`mr-6 w-5 h-5 has-tooltip rounded-full ${color.css_hex}`]" />

                    {{ color.name }}
                  </button>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
      <div class="flex flex-row justify-end w-full gap-2 pt-20">
        <span class="w-1/2" />
        <button
          type="button"
          class="w-1/4 m-0 cancel-btn"
          @click="
            () => {
              $emit('cancel')
            }
          ">
          {{ $t("Cancel") }}
        </button>
        <button type="button" class="w-1/4 primary-btn" @click="saveNote">{{ $t("Save") }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import TextInput from "@/components/shared/TextInput.vue"
import TextEditor from "@/components/shared/TextEditor.vue"

import { MenuButton, Menu, MenuItem, MenuItems } from "@headlessui/vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: { TextInput, TextEditor, MenuButton, Menu, MenuItem, MenuItems },
  props: {
    label: null,
    description: null,
    backgroundColor: null,
    id: null
  },
  emits: ["done", "cancel"],
  data() {
    return {
      new_title: "",
      new_description: "",
      new_bg_color: "",
      color_palettes: [
        { css_hex: "bg-transparent", hex: "transparent", name: this.$t("no color") },
        { css_hex: "bg-[#FAF4AF]", hex: "#FAF4AF", name: this.$t("yellow") },
        { css_hex: "bg-[#93D076]", hex: "#93D076", name: this.$t("green") },
        { css_hex: "bg-[#CDADD7]", hex: "#CDADD7", name: this.$t("purple") },
        { css_hex: "bg-[#6ED7F8]", hex: "#6ED7F8", name: this.$t("blue") },
        { css_hex: "bg-[#F0949E]", hex: "#F0949E", name: this.$t("red") },
        { css_hex: "bg-white", hex: "white", name: this.$t("white") },
        { css_hex: "bg-black", hex: "black", name: this.$t("black") },
        { css_hex: "bg-slate-200", hex: "rgb(226 232 240)", name: this.$t("gray") }
      ],
      connect_to: "all_pages",
      page_identifier: "all"
    }
  },
  watch: {
    connect_to() {
      if (this.connect_to == "this_page") {
        this.page_identifier = `${this.$route.params.object_type}>${this.$route.params.cid}`
      } else {
        this.page_identifier = "all"
      }
    }
  },

  created() {
    if (typeof this.label == "object") {
      this.connect_to = "this_page"
    }
    this.new_title = this.$utils.extractValue(this.label, "", this.$route)
    this.new_description = this.$utils.extractValue(this.description, "", this.$route)
    this.new_bg_color = this.$utils.extractValue(this.backgroundColor, "transparent", this.$route)
  },

  methods: {
    handleUpdate() {
      this.$emitter.emit(ALL_EMITTERS.UPDATE_TEXT_INSTANCE, {
        label: this.new_title,
        description: this.new_description,
        backgroundColor: this.new_bg_color,
        id: this.id,
        component_type: "text",
        connectTo: this.connect_to,
        page_identifier: this.page_identifier
      })
    },
    saveNote() {
      this.handleUpdate()
      this.$emit("done")
    }
  }
}
</script>
